import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { QuartzJobDto, TriggerResultDto } from '@/types/dto';

function fmtTs(iso: string | null): string {
  if (!iso) return '—';
  const delta = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(delta);
  const dir = delta < 0 ? 'ago' : 'in';
  const m = Math.round(abs / 60_000);
  if (m < 60) return `${m}m ${dir}`;
  const h = Math.round(abs / 3_600_000);
  if (h < 48) return `${h}h ${dir}`;
  const d = Math.round(abs / 86_400_000);
  return `${d}d ${dir}`;
}

export function QuartzTable({ jobs }: { jobs: QuartzJobDto[] }) {
  const qc = useQueryClient();
  const trigger = useMutation({
    mutationFn: (key: string) =>
      api.post<TriggerResultDto>(`/api/debug/triggers/${encodeURIComponent(key)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['debug'] }),
  });

  return (
    <Card>
      <h2 className="text-xl font-semibold mb-3">Quartz Schedule <span className="text-muted text-sm font-normal">(auto-refresh 8s)</span></h2>
      {jobs.length === 0 ? (
        <p className="text-muted">Worker unreachable or no jobs registered.</p>
      ) : (
        <>
          {/* Desktop */}
          <table className="hidden md:table w-full text-sm">
            <thead>
              <tr className="border-b-2 border-accent">
                <Th>Job</Th><Th>Cron</Th><Th>Last fire</Th><Th>Next fire</Th><Th right>Run</Th>
              </tr>
            </thead>
            <tbody>
              {[...jobs].sort((a, b) => a.key.localeCompare(b.key)).map((j) => (
                <tr key={j.key} className="border-b border-border hover:bg-surface-hover">
                  <td className="py-3 px-3 font-medium">{j.key}</td>
                  <td className="py-3 px-3 text-muted font-mono text-xs">{j.cron ?? '—'}</td>
                  <td className="py-3 px-3 text-muted">{fmtTs(j.previousFire)}</td>
                  <td className="py-3 px-3 text-muted">{fmtTs(j.nextFire)}</td>
                  <td className="py-3 px-3 text-right">
                    <Button size="sm" disabled={trigger.isPending} onClick={() => trigger.mutate(j.key)}>▶ Run</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile */}
          <ul className="md:hidden flex flex-col divide-y divide-border">
            {[...jobs].sort((a, b) => a.key.localeCompare(b.key)).map((j) => (
              <li key={j.key} className="py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{j.key}</div>
                    <div className="text-muted font-mono text-xs mt-0.5 break-all">{j.cron ?? '—'}</div>
                    <div className="text-muted text-[0.7rem] mt-1 flex gap-3">
                      <span>last: {fmtTs(j.previousFire)}</span>
                      <span>next: {fmtTs(j.nextFire)}</span>
                    </div>
                  </div>
                  <Button size="sm" disabled={trigger.isPending} onClick={() => trigger.mutate(j.key)}>▶</Button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th className={`py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui) ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}
